import { AppShell } from "./components/AppShell";
import { PageLoader } from "./components/ui";
import { useAuth } from "./context/AuthContext";
import { Redirect, useRouter } from "./lib/router";
import { rememberReturnPath } from "./lib/returnPath";
import { AccessRequestDetailPage } from "./pages/AccessRequestDetailPage";
import { AccessRequestPage } from "./pages/AccessRequestPage";
import { ChecklistFormatacaoDetailPage } from "./pages/ChecklistFormatacaoDetailPage";
import { ChecklistFormatacaoListPage } from "./pages/ChecklistFormatacaoListPage";
import { ChecklistFormatacaoPage } from "./pages/ChecklistFormatacaoPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ActivateFirstAccessPage, FirstAccessPage } from "./pages/FirstAccessPage";
import { LoginOtpPage } from "./pages/LoginOtpPage";
import { LoginPage } from "./pages/LoginPage";
import { NewTicketPage } from "./pages/NewTicketPage";
import { RatListPage } from "./pages/RatListPage";
import { RatPage } from "./pages/RatPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import {
  PasswordResetConfirmPage,
  PasswordResetRequestPage,
} from "./pages/PasswordResetPage";
import { TicketDetailPage } from "./pages/TicketDetailPage";
import { TicketsPage } from "./pages/TicketsPage";
import { UserManagementPage } from "./pages/UserManagementPage";

function ProtectedArea({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isReady } = useAuth();
  const { pathname } = useRouter();

  if (!isReady) return <PageLoader />;
  if (!isAuthenticated) {
    rememberReturnPath(pathname);
    return <Redirect to="/login" />;
  }

  return <AppShell>{children}</AppShell>;
}

export function App() {
  const { pathname } = useRouter();
  const { isAuthenticated, session } = useAuth();

  if (pathname === "/login") {
    return isAuthenticated ? <Redirect to="/" /> : <LoginPage />;
  }
  if (pathname === "/login/verificacao") {
    return isAuthenticated ? <Redirect to="/" /> : <LoginOtpPage />;
  }
  if (pathname === "/primeiro-acesso") {
    return isAuthenticated ? <Redirect to="/" /> : <FirstAccessPage />;
  }
  if (pathname === "/primeiro-acesso/ativar") {
    return isAuthenticated ? <Redirect to="/" /> : <ActivateFirstAccessPage />;
  }
  if (pathname === "/recuperar-senha") {
    return isAuthenticated ? <Redirect to="/" /> : <PasswordResetRequestPage />;
  }
  if (pathname === "/recuperar-senha/confirmar") {
    return isAuthenticated ? <Redirect to="/" /> : <PasswordResetConfirmPage />;
  }

  let page: React.ReactNode;
  if (pathname === "/") page = <DashboardPage />;
  else if (pathname === "/chamados") page = <TicketsPage />;
  else if (pathname === "/chamados/novo") page = <NewTicketPage />;
  else if (pathname === "/usuarios") {
    page = session?.podeGerenciarUsuarios && session?.codigoEmpresa === 52
      ? <UserManagementPage />
      : <NotFoundPage />;
  }
  else if (pathname === "/acessos") {
    page = session?.podeGerenciarUsuarios || session?.podeSolicitarAcesso
      ? <AccessRequestPage />
      : <NotFoundPage />;
  }
  else if (pathname === "/checklist-formatacao") {
    page = session?.codigoEmpresa === 52
      ? <ChecklistFormatacaoPage />
      : <NotFoundPage />;
  }
  else if (pathname === "/checklist-formatacao/lista") {
    page = session?.codigoEmpresa === 52
      ? <ChecklistFormatacaoListPage />
      : <NotFoundPage />;
  }
  else if (pathname === "/rat/novo") {
    page = session?.codigoEmpresa === 52
      ? <RatPage />
      : <NotFoundPage />;
  }
  else if (pathname === "/rat/lista") {
    page = session?.codigoEmpresa === 52
      ? <RatListPage />
      : <NotFoundPage />;
  }

  else {
    const ticketMatch = pathname.match(/^\/chamados\/(\d+)$/);
    const checklistMatch = pathname.match(/^\/checklist-formatacao\/(\d+)$/);
    const checklistEditarMatch = pathname.match(/^\/checklist-formatacao\/(\d+)\/editar$/);
    const acessoMatch = pathname.match(/^\/acessos\/(\d+)$/);
    const ratEditarMatch = pathname.match(/^\/rat\/(\d+)\/editar$/);
    if (ticketMatch) {
      page = <TicketDetailPage ticketId={ticketMatch[1]} />;
    } else if (checklistEditarMatch) {
      page = session?.codigoEmpresa === 52
        ? <ChecklistFormatacaoPage id={checklistEditarMatch[1]} />
        : <NotFoundPage />;
    } else if (checklistMatch) {
      page = session?.codigoEmpresa === 52
        ? <ChecklistFormatacaoDetailPage id={checklistMatch[1]} />
        : <NotFoundPage />;
    } else if (acessoMatch) {
      page = session?.codigoEmpresa === 52
        ? <AccessRequestDetailPage id={acessoMatch[1]} />
        : <NotFoundPage />;
    } else if (ratEditarMatch) {
      page = session?.codigoEmpresa === 52
        ? <RatPage id={ratEditarMatch[1]} />
        : <NotFoundPage />;
    } else {
      page = <NotFoundPage />;
    }
  }

  return <ProtectedArea>{page}</ProtectedArea>;
}
